import type {
    BatchMoveItem,
    BoardActionItemDto,
    BoardCommentDto,
    BoardDto,
    BoardItemDto,
    BoardLiveEvents,
    BoardStateDto,
    ToggleVoteResultDto,
} from '../../../../shared/types/board';

/**
 * Pure reducers that apply live events (and our own mutation results) to the cached board state.
 * Every reducer is idempotent: our own changes arrive twice (HTTP response, then the live echo).
 */

export function applyBoardUpdated(state: BoardStateDto, board: BoardDto): BoardStateDto {
    return { ...state, board };
}

export function applyItemCreated(state: BoardStateDto, item: BoardItemDto): BoardStateDto {
    if (state.items.some(i => i._id === item._id)) return state;
    return { ...state, items: [...state.items, item] };
}

export function applyItemUpdated(state: BoardStateDto, { itemId, changes }: BoardLiveEvents['item_updated'], skip?: ReadonlySet<string>): BoardStateDto {
    if (skip?.has(itemId) || !state.items.some(i => i._id === itemId)) return state;
    return { ...state, items: state.items.map(i => (i._id === itemId ? { ...i, ...changes } : i)) };
}

export function applyItemsMoved(state: BoardStateDto, moves: BatchMoveItem[], skip?: ReadonlySet<string>): BoardStateDto {
    const relevant = skip?.size ? moves.filter(m => !skip.has(m.itemId)) : moves;
    if (!relevant.length) return state;
    const byId = new Map(relevant.map(m => [m.itemId, m]));
    return {
        ...state,
        items: state.items.map(i => {
            const m = byId.get(i._id);
            if (!m) return i;
            return {
                ...i,
                x: m.x,
                y: m.y,
                ...(m.width !== undefined ? { width: m.width } : {}),
                ...(m.height !== undefined ? { height: m.height } : {}),
            };
        }),
    };
}

/**
 * An item is gone, with its comment pins. Connectors attached to it go too: the server deletes
 * them and sends their own item_deleted events, which then find nothing left to do.
 */
export function applyItemDeleted(state: BoardStateDto, itemId: string): BoardStateDto {
    if (!state.items.some(i => i._id === itemId)) return state;
    const gone = new Set([itemId]);
    for (const i of state.items) {
        if (i.type === 'connector' && (i.start?.itemId === itemId || i.end?.itemId === itemId)) gone.add(i._id);
    }
    return {
        ...state,
        items: state.items.filter(i => !gone.has(i._id)),
        comments: state.comments.filter(c => !c.itemId || !gone.has(c.itemId)),
    };
}

export function applyCommentAdded(state: BoardStateDto, comment: BoardCommentDto): BoardStateDto {
    if (state.comments.some(c => c._id === comment._id)) return state;
    return { ...state, comments: [...state.comments, comment] };
}

/** A comment and all its replies. */
export function commentSubtree(comments: BoardCommentDto[], rootId: string): string[] {
    const found = new Set([rootId]);
    let changed = true;
    while (changed) {
        changed = false;
        for (const c of comments) {
            if (c.parentCommentId && found.has(c.parentCommentId) && !found.has(c._id)) {
                found.add(c._id);
                changed = true;
            }
        }
    }
    return [...found];
}

export function applyCommentsMoved(state: BoardStateDto, commentIds: string[], x: number, y: number): BoardStateDto {
    const ids = new Set(commentIds);
    return { ...state, comments: state.comments.map(c => (ids.has(c._id) ? { ...c, x, y } : c)) };
}

export function applyCommentsDeleted(state: BoardStateDto, commentIds: string[]): BoardStateDto {
    const ids = new Set(commentIds);
    if (!state.comments.some(c => ids.has(c._id))) return state;
    return { ...state, comments: state.comments.filter(c => !ids.has(c._id)) };
}

/** Our own dot was placed or taken back. */
export function applyVoteChanged(state: BoardStateDto, { itemId, selfVotes }: ToggleVoteResultDto): BoardStateDto {
    return { ...state, items: state.items.map(i => (i._id === itemId ? { ...i, selfVotes } : i)) };
}

export function applyVotingChanged(state: BoardStateDto, event: BoardLiveEvents['voting_changed']): BoardStateDto {
    const { phase } = event.votingConfig;
    const { dotsUsedBy, ...rest } = state;
    return {
        ...rest,
        // Who has voted how much: gone with voting, emptied with the dots
        ...(phase ? { dotsUsedBy: event.votesCleared ? {} : dotsUsedBy ?? {} } : {}),
        board: { ...state.board, votingConfig: event.votingConfig },
        items: state.items.map(item => {
            if (item.type !== 'sticky') return item;
            const { selfVotes, totalVotes: _hidden, ...rest } = item;
            if (!phase) return rest;
            const self = event.votesCleared ? (selfVotes === undefined ? undefined : 0) : selfVotes;
            const next: BoardItemDto = { ...rest };
            if (self !== undefined) next.selfVotes = self;
            if (phase === 'closed') next.totalVotes = event.totals?.[item._id] ?? 0;
            return next;
        }),
    };
}

/** How many dots each person has placed (never where). */
export function applyVotingProgress(state: BoardStateDto, { dotsUsedBy }: BoardLiveEvents['voting_progress']): BoardStateDto {
    return state.board.votingConfig.phase ? { ...state, dotsUsedBy } : state;
}

/** Ideas were combined or separated: the new combinations, and our own dots on the stickies involved. */
export function applyIdeasCombined(state: BoardStateDto, { combined, selfVotes }: BoardLiveEvents['ideas_combined']): BoardStateDto {
    return {
        ...state,
        board: { ...state.board, combined },
        items: state.items.map(i => (i._id in selfVotes ? { ...i, selfVotes: selfVotes[i._id] } : i)),
    };
}

/** The sticky each sticky is voted on as: the one it was combined into (if that's still here), or itself. */
export function ideaIds(state: { board: Partial<Pick<BoardDto, 'combined'>>; items: Array<Pick<BoardItemDto, '_id'>> }): Map<string, string> {
    const present = new Set(state.items.map(i => i._id));
    const ideas = new Map<string, string>();
    for (const c of state.board.combined ?? []) if (present.has(c.itemId) && present.has(c.intoId)) ideas.set(c.itemId, c.intoId);
    return ideas;
}

export function applyActionItemChanged(state: BoardStateDto, item: BoardActionItemDto): BoardStateDto {
    const exists = state.actionItems.some(a => a._id === item._id);
    return {
        ...state,
        actionItems: exists ? state.actionItems.map(a => (a._id === item._id ? item : a)) : [...state.actionItems, item],
    };
}

export function applyActionItemDeleted(state: BoardStateDto, actionItemId: string): BoardStateDto {
    return { ...state, actionItems: state.actionItems.filter(a => a._id !== actionItemId) };
}

/** How many dots the caller has placed. */
export function dotsUsed(state: BoardStateDto): number {
    return state.items.reduce((n, i) => n + (i.selfVotes ?? 0), 0);
}
